import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Printer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { HUB_PROPERTIES, HUB_PAGES, hubUrl } from './guest-hub/hubConfig';
import { QR_LOGO_DATA_URI } from './guest-hub/HubBrand';

const SIGN_COPY = {
  wifi: { title: 'Scan for Wi-Fi', sub: 'Connect in seconds. No typing passwords.' },
  guidebook: { title: 'Scan for House Guidebook', sub: 'Check-in, house rules, how-tos, photos & videos.' },
  'local-guide': { title: 'Scan for Local Favorites', sub: 'Our picks for coffee, food & sights.' },
  social: { title: 'Loved your stay?', sub: 'Follow @capitolviewstay & leave a review.' },
  book: { title: 'Book Direct & Save 35%', sub: 'Use code CVS101 on your next stay.' },
};

const MODES = [
  { key: 'pages', label: 'Every page (4x6)' },
  { key: 'wifi', label: 'Wi-Fi only (4x6)' },
  { key: 'guidebook', label: 'Guidebook only (4x6)' },
  { key: 'combined', label: 'Combined sheet (Letter)' },
];
const SINGLE_PAGE_MODES = ['wifi', 'guidebook'];

const QR = ({ slug, page, size }) => (
  <QRCodeSVG value={hubUrl(slug, page)} size={size} level="H" marginSize={1} fgColor="#0f172a" bgColor="#ffffff"
    imageSettings={{ src: QR_LOGO_DATA_URI, height: size * 0.22, width: size * 0.22, excavate: true }} />
);

const Wordmark = ({ small }) => (
  <div className="text-center">
    <div className={`font-serif font-bold tracking-tight text-slate-900 ${small ? 'text-lg' : 'text-2xl'}`}>CAPITOL VIEW</div>
    <div className="text-[9px] tracking-[0.4em] uppercase text-amber-600 -mt-0.5">Stays</div>
  </div>
);

const GUIDEBOOK_BULLETS = ['Wi-Fi & check-in', 'House rules', 'How things work', 'Photos & video tour'];

const TentSign = ({ prop, page }) => (
  <div data-testid={`sign-${prop.slug}-${page}`} className="sign-4x6 bg-white border border-slate-200 flex flex-col items-center justify-between text-center p-6">
    <Wordmark />
    <div>
      <h2 className="text-2xl font-serif font-bold text-slate-900 leading-tight">{SIGN_COPY[page].title}</h2>
      <p className="text-slate-500 text-sm mt-1">{SIGN_COPY[page].sub}</p>
      {page === 'guidebook' && (
        <ul className="flex flex-wrap justify-center gap-x-3 gap-y-0.5 mt-2 text-[11px] text-slate-600" data-testid={`sign-guidebook-bullets-${prop.slug}`}>
          {GUIDEBOOK_BULLETS.map((b) => <li key={b} className="before:content-['•'] before:text-amber-500 before:mr-1">{b}</li>)}
        </ul>
      )}
    </div>
    <QR slug={prop.slug} page={page} size={190} />
    <div>
      <p className="text-xs uppercase tracking-[0.25em] text-amber-600 font-semibold">{prop.name}</p>
      <p className="text-[10px] text-slate-400 mt-1">Point your phone camera at the code</p>
    </div>
  </div>
);

const CombinedSheet = ({ prop }) => (
  <div data-testid={`sign-combined-${prop.slug}`} className="sign-letter bg-white border border-slate-200 p-10 flex flex-col">
    <Wordmark />
    <h2 className="text-3xl font-serif font-bold text-slate-900 text-center mt-4">Welcome to {prop.name}</h2>
    <p className="text-slate-500 text-center mt-1">Scan any code with your phone camera</p>
    <div className="grid grid-cols-3 gap-6 mt-8 flex-1 content-center">
      {HUB_PAGES.map((pg) => (
        <div key={pg.key} className="flex flex-col items-center text-center">
          <QR slug={prop.slug} page={pg.key} size={150} />
          <p className="font-semibold text-slate-900 mt-2 text-sm">{SIGN_COPY[pg.key].title}</p>
          <p className="text-slate-500 text-xs">{SIGN_COPY[pg.key].sub}</p>
        </div>
      ))}
    </div>
    <p className="text-center text-xs text-slate-400 mt-6">Questions? Call or text (571) 507-5650</p>
  </div>
);

const ChipRow = ({ items, value, onChange, prefix }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((i) => (
      <Button key={i.key} size="sm" data-testid={`${prefix}-${i.key}`} onClick={() => onChange(i.key)}
        className={value === i.key ? 'bg-amber-500 hover:bg-amber-600 text-slate-900' : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-100'}>
        {i.label}
      </Button>
    ))}
  </div>
);

export default function QRSigns() {
  const [params] = useSearchParams();
  const validMode = MODES.some((m) => m.key === params.get('mode')) ? params.get('mode') : 'pages';
  const validProp = HUB_PROPERTIES.some((p) => p.slug === params.get('prop')) ? params.get('prop') : 'all';
  const [mode, setMode] = useState(validMode);
  const [prop, setProp] = useState(validProp);
  const props = prop === 'all' ? HUB_PROPERTIES : HUB_PROPERTIES.filter((p) => p.slug === prop);
  const pages = SINGLE_PAGE_MODES.includes(mode) ? [mode] : HUB_PAGES.map((p) => p.key);

  return (
    <div data-testid="qr-signs-page" className="min-h-screen bg-slate-100 pt-28 pb-16">
      <div className="max-w-7xl mx-auto px-4">
        <div className="no-print space-y-4 mb-10">
          <h1 className="text-4xl sm:text-5xl font-serif font-bold text-slate-900">Printable Signs</h1>
          <p className="text-slate-600 max-w-2xl">Print on cardstock. 4x6 signs fit standard table tents and frames. Choose "Save as PDF" in the print dialog to send to a print shop.</p>
          <ChipRow prefix="signs-mode" items={MODES} value={mode} onChange={setMode} />
          <ChipRow prefix="signs-prop" items={[{ key: 'all', label: 'All properties' }, ...HUB_PROPERTIES.map((p) => ({ key: p.slug, label: p.name }))]} value={prop} onChange={setProp} />
          <Button data-testid="signs-print-btn" onClick={() => window.print()} className="bg-slate-900 hover:bg-slate-800">
            <Printer className="h-4 w-4 mr-2" />Print signs
          </Button>
        </div>
        <div className="sign-grid flex flex-wrap gap-6 justify-center">
          {mode === 'combined'
            ? props.map((p) => <CombinedSheet key={p.slug} prop={p} />)
            : props.flatMap((p) => pages.map((pg) => <TentSign key={`${p.slug}-${pg}`} prop={p} page={pg} />))}
        </div>
      </div>
      <style>{`
        .sign-4x6 { width: 4in; height: 6in; }
        .sign-letter { width: 8.5in; height: 11in; }
        @media print {
          @page { size: ${mode === 'combined' ? 'letter' : '4in 6in'}; margin: 0; }
          header, footer, .no-print { display: none !important; }
          [data-testid="qr-signs-page"] { padding: 0 !important; background: #fff !important; }
          .sign-grid { display: block !important; }
          .sign-4x6, .sign-letter { border: none !important; break-after: page; page-break-after: always; margin: 0 auto; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}
